import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";

export default function Login() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && user) nav("/dashboard", { replace: true }); }, [user, loading, nav]);
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: "var(--pm-gradient)" }}>
        <Link to="/" className="flex items-center gap-3 font-extrabold text-xl"><LogoMark /> PinMyDates</Link>
        <div>
          <h2 className="text-5xl font-extrabold leading-tight">{BRAND.headline}</h2>
          <p className="mt-4 text-white/85 max-w-md">{BRAND.subheading}</p>
        </div>
        <p className="font-bold">{BRAND.slogan}</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[var(--pm-bg)]">
        <div className="w-full max-w-md pm-card p-8 sm:p-10 pm-fade-up" data-testid="login-card">
          <img src={BRAND.logo} alt="PinMyDates — Every Date Matters." className="w-28 h-28 rounded-3xl mx-auto mb-6 object-cover" />
          <h1 className="text-3xl font-extrabold text-center">Welcome to PinMyDates</h1>
          <p className="text-center text-[var(--pm-muted)] mt-2 text-sm">Sign in or create your account in one step.</p>
          <button onClick={() => startGoogleLogin("/dashboard")} className="pm-btn pm-btn-outline w-full h-12 mt-8 text-[0.95rem]" data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <p className="flex items-start gap-2 text-xs text-[var(--pm-muted)] mt-6 bg-[#F6F4FD] rounded-xl p-3">
            <ShieldCheck className="w-4 h-4 shrink-0 text-[var(--pm-success)]" />
            Your password is never stored by PinMyDates. Account recovery is handled by your Google account.
          </p>
          <p className="text-xs text-center text-[var(--pm-muted)] mt-6">
            By continuing you agree to our <Link to="/terms-of-service" className="text-[var(--pm-primary)] font-semibold">Terms</Link> and <Link to="/privacy-policy" className="text-[var(--pm-primary)] font-semibold">Privacy Policy</Link>.
          </p>
          <Link to="/" className="block text-center text-sm font-semibold mt-6 text-[var(--pm-muted)] hover:text-[var(--pm-primary)]" data-testid="login-back-home">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
